import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Table,
    Button,
    Input,
    Space,
    Modal,
    Form,
    InputNumber,
    Select,
    Popconfirm,
    message,
    Card,
    Typography,
    Row,
    Col
} from 'antd';
import {
    SearchOutlined,
    PlusOutlined,
    ExportOutlined,
    ImportOutlined,
    EditOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import Layout from './Layout';

const { Title } = Typography;

const NomenclaturePage = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [priceTypes, setPriceTypes] = useState([]);
    const [articles, setArticles] = useState([]);

    const [searchText, setSearchText] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [sortField, setSortField] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');

    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form] = Form.useForm();

    const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, item: null });
    const fileInputRef = useRef(null);

    useEffect(() => {
        fetchData();
        fetchPriceTypes();
        fetchArticles();
    }, [pagination.current, pagination.pageSize, sortField, sortOrder]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/nomenclature', {
                params: {
                    page: pagination.current,
                    limit: pagination.pageSize,
                    search: searchText,
                    sortField,
                    sortOrder
                },
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setData(response.data.data || []);
            setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
        } catch (error) {
            message.error('Ошибка при загрузке номенклатуры');
        } finally {
            setLoading(false);
        }
    };

    const fetchPriceTypes = async () => {
        try {
            const response = await axios.get('/api/price-types?limit=1000', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            setPriceTypes(response.data.data || []);
        } catch (e) { console.error(e); }
    };

    const fetchArticles = async () => {
        try {
            const response = await axios.get('/api/financial-articles?limit=1000', {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            setArticles(response.data.data || []);
        } catch (e) { console.error(e); }
    };

    const handleTableChange = (pag, filters, sorter) => {
        setPagination(pag);
        if (sorter.field) {
            setSortField(sorter.field);
            setSortOrder(sorter.order === 'descend' ? 'desc' : 'asc');
        }
    };

    const showModal = (item = null) => {
        if (item) {
            setEditingId(item.id);
            const formPrices = {};
            if (item.prices) {
                Object.keys(item.prices).forEach(key => {
                    formPrices[`price_${key}`] = item.prices[key];
                });
            }
            form.setFieldsValue({
                name: item.name,
                article_id: item.article_id,
                unit: item.unit,
                ...formPrices
            });
        } else {
            setEditingId(null);
            form.resetFields();
        }
        setIsModalVisible(true);
    };

    const handleFormSubmit = async (values) => {
        try {
            const prices = {};
            priceTypes.forEach(pt => {
                if (values[`price_${pt.id}`] !== undefined) prices[pt.id] = values[`price_${pt.id}`];
            });
            const payload = { ...values, prices };
            const token = localStorage.getItem('token');
            if (editingId) {
                await axios.put(`/api/nomenclature/${editingId}`, payload, { headers: { 'Authorization': `Bearer ${token}` } });
                message.success('Обновлено');
            } else {
                await axios.post('/api/nomenclature', payload, { headers: { 'Authorization': `Bearer ${token}` } });
                message.success('Добавлено');
            }
            setIsModalVisible(false);
            fetchData();
        } catch (error) { message.error('Ошибка сохранения'); }
    };

    const handleDelete = async (id) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/nomenclature/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
            message.success('Удалено');
            fetchData();
        } catch (e) { message.error('Ошибка удаления'); }
    };

    const handleExport = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get('/api/nomenclature?limit=10000', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const dataToExport = response.data.data.map(item => {
                const row = {
                    'ID': item.id,
                    'Название': item.name,
                    'Статья': articles.find(a => a.id === item.article_id)?.name || '-',
                    'Ед. изм.': item.unit,
                };
                priceTypes.forEach(pt => { 
                    // ИСПРАВЛЕНО: Строгая проверка на существование значения
                    const val = item.prices?.[pt.id];
                    row[pt.name] = (val !== undefined && val !== null && val !== '') ? val : ''; 
                });
                row['Удалить'] = 'Нет';
                return row;
            });
            const ws = XLSX.utils.json_to_sheet(dataToExport);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Номенклатура");
            XLSX.writeFile(wb, 'номенклатура.xlsx');
        } catch (e) { message.error('Ошибка экспорта'); }
    };

    const handleImport = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const workbook = XLSX.read(bstr, { type: 'binary' });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]];
                const jsonData = XLSX.utils.sheet_to_json(worksheet);
                const transformed = jsonData.map(row => {
                    const prices = {};
                    priceTypes.forEach(pt => { 
                        // ИСПРАВЛЕНО: Проверяем наличие значения в колонке Excel (включая 0)
                        const cellValue = row[pt.name];
                        if (cellValue !== undefined && cellValue !== null && cellValue !== '') {
                            prices[pt.id] = parseFloat(cellValue); 
                        }
                    });
                    return { 
                        id: row['ID'] || null, 
                        name: row['Название'], 
                        article_name: row['Статья'], 
                        unit: row['Ед. изм.'], 
                        prices, 
                        delete_record: row['Удалить'] === 'Да' 
                    };
                });
                const token = localStorage.getItem('token');
                await axios.post('/api/nomenclature/import', transformed, { headers: { 'Authorization': `Bearer ${token}` } });
                message.success('Данные импортированы');
                fetchData();
            } catch (err) { message.error('Ошибка импорта'); }
        };
        reader.readAsBinaryString(file);
        e.target.value = '';
    };

    const columns = [
        {
            title: 'Действия',
            key: 'actions',
            fixed: 'left',
            width: 80,
            align: 'center',
            render: (_, record) => (
                <Space size="small">
                    <Button type="text" size="small" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => showModal(record)} />
                    <Popconfirm title="Удалить?" onConfirm={() => handleDelete(record.id)}>
                        <Button type="text" size="small" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
                    </Popconfirm>
                </Space>
            )
        },
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
            fixed: 'left',
            sorter: true
        },
        {
            title: 'Название',
            dataIndex: 'name',
            key: 'name',
            width: 400,
            fixed: 'left',
            sorter: true,
            //ellipsis: true
            render: (text) => (
                <div style={{ 
                    whiteSpace: 'normal', 
                    wordBreak: 'break-word', 
                    lineHeight: '1.4',
                    padding: '4px 0' 
                }}>
                    {text}
                </div>
            )
        },
        {
            title: 'Статья',
            dataIndex: 'article_id',
            key: 'article_id',
            width: 200,
            render: (id) => articles.find(a => a.id === id)?.name || '-'
        },
        {
            title: 'Ед. изм.',
            dataIndex: 'unit',
            key: 'unit',
            width: 100
        },
        ...priceTypes.map(pt => ({
            title: pt.name,
            key: `price_${pt.id}`,
            width: 140,
            align: 'right',
            render: (_, record) => {
                const price = record.prices?.[pt.id];
                // ИСПРАВЛЕНО: Если цена 0, выводим её, если пусто/null - выводим "-"
                return (price !== undefined && price !== null && price !== '')
                    ? `${parseFloat(price).toLocaleString('ru-RU', { minimumFractionDigits: 2 })}`
                    : '-';
            }
        })),
    ];

    const scrollX = columns.reduce((sum, col) => sum + (parseInt(col.width) || 150), 0);

    return (
        <Layout>
            <div style={{ padding: '0px', width: '100%', maxWidth: '98vw', overflow: 'hidden' }}>
                <Card bordered={false} bodyStyle={{ padding: '16px' }} style={{ width: '100%', borderRadius: 0 }}>
                    {/* Заголовок и кнопки смещены влево */}
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 16, alignItems: 'center', gap: '20px' }}>
                        <Title level={4} style={{ margin: 0, whiteSpace: 'nowrap' }}>Номенклатура</Title>
                        <Space wrap size="middle">
                            <Input.Search
                                placeholder="Поиск..."
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                onSearch={() => { setPagination({ ...pagination, current: 1 }); fetchData(); }}
                                style={{ width: 250 }}
                                enterButton
                            />
                            <Button icon={<ExportOutlined />} onClick={handleExport}>Экспорт</Button>
                            <Button icon={<ImportOutlined />} onClick={() => fileInputRef.current.click()}>Импорт</Button>
                            <Button type="primary" icon={<PlusOutlined />} onClick={() => showModal()}>Добавить</Button>
                            <input type="file" ref={fileInputRef} hidden onChange={handleImport} accept=".xlsx,.xls" />
                        </Space>
                    </div>

                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey="id"
                        loading={loading}
                        size="small"
                        bordered
                        tableLayout="fixed" // Фиксирует ширину, предотвращая расползание
                        pagination={{
                            ...pagination,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '50', '100'],
                            showTotal: (total) => `Всего: ${total}`
                        }}
                        onChange={handleTableChange}
                        scroll={{
                            x: scrollX, 
                            y: 'calc(100vh - 280px)' 
                        }}
                        onRow={(record) => ({
                            onContextMenu: (e) => {
                                e.preventDefault();
                                setContextMenu({ visible: true, x: e.clientX, y: e.clientY, item: record });
                            }
                        })}
                    />
                </Card>
            </div>

            <Modal
                title={editingId ? "Редактирование" : "Создание"}
                open={isModalVisible}
                onOk={() => form.submit()}
                onCancel={() => setIsModalVisible(false)}
                width={850}
                style={{ top: 20 }}
            >
                <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
                    <Row gutter={16}>
                        <Col span={18}>
                            <Form.Item name="name" label="Название" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={6}>
                            <Form.Item name="unit" label="Ед. изм." rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item name="article_id" label="Финансовая статья">
                                <Select
                                    showSearch
                                    placeholder="Выберите статью"
                                    options={articles.map(a => ({ label: a.name, value: a.id }))}
                                    filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
                                    allowClear
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Title level={5} style={{ marginTop: 10 }}>Цены</Title>
                    <Row gutter={[16, 8]}>
                        {priceTypes.map(pt => (
                            <Col span={8} key={pt.id}>
                                <Form.Item name={`price_${pt.id}`} label={pt.name} style={{ marginBottom: 8 }}>
                                    <InputNumber style={{ width: '100%' }} precision={2} placeholder="0.00" />
                                </Form.Item>
                            </Col>
                        ))}
                    </Row>
                </Form>
            </Modal>

            {contextMenu.visible && (
                <div
                    style={{
                        position: 'fixed', top: contextMenu.y, left: contextMenu.x,
                        backgroundColor: '#fff', boxShadow: '0 3px 12px rgba(0,0,0,0.15)',
                        zIndex: 2000, borderRadius: '4px', padding: '4px 0', minWidth: '180px',
                        border: '1px solid #f0f0f0'
                    }}
                    onClick={() => setContextMenu({ ...contextMenu, visible: false })}
                >
                    <div className="ctx-item" onClick={() => showModal(contextMenu.item)}>
                        <EditOutlined /> Редактировать
                    </div>
                    <Popconfirm title="Удалить?" onConfirm={() => handleDelete(contextMenu.item.id)}>
                        <div className="ctx-item" style={{ color: '#ff4d4f' }}>
                            <DeleteOutlined /> Удалить
                        </div>
                    </Popconfirm>
                </div>
            )}

            <style>{`
                .ctx-item {
                    padding: 10px 16px;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 10px;
                }
                .ctx-item:hover { background-color: #f5f5f5; }
                .ant-table-thead > tr > th { white-space: nowrap; }
                /* --- НОВЫЕ СТИЛИ ДЛЯ ПЕРЕНОСА ЗАГОЛОВКОВ --- */
                    .ant-table-thead > tr > th { 
                        white-space: normal !important; /* Разрешаем перенос */
                        vertical-align: top !important; /* Выравниваем по верху */
                        line-height: 1.2 !important;   /* Уплотняем строки в заголовках */
                        height: auto !important;
                        padding: 8px 4px !important;    /* Немного уменьшаем боковые отступы */
                    }

                    /* Стили для того, чтобы зафиксированные колонки не "разлетались" */
                    .ant-table-cell-fix-left, .ant-table-cell-fix-right {
                        background: #fff !important;
                    }
            `}</style>
        </Layout>
    );
};

export default NomenclaturePage;